import React, { useState, useEffect, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { X, Plus, Trash2, Repeat, FileText, Eye } from 'lucide-react';
import { fetchTransportes, selectAllTransportes } from '../../store/slices/transportesSlice';
import { selectSeason } from '../../store/slices/seasonSlice';
import { inventarioAPI } from '../../services/api';
import WeekHighlightDatePicker from '../Export/WeekHighlightDatePicker';
import { getFirstBusinessDayOfWeek } from '../../utils/dateUtils';
import { checkOrderLimits } from '../../utils/orderLimits';
import { AbilityContext } from '../../contexts/AbilityContext';
import { useToast } from '../Toast/ToastContainer';
import { calculateOrderItemTotal, formatPrice, getCurrency, getPriceField, getPriceLabel } from '../../utils/pricingCalculations';

const OrderModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  order = null,
  clientes = [],
  productos = [],
  temporada = null,
  pedidosRecurrentes = [],
  existingOrders = [],
  readOnly = false
}) => {
  const dispatch = useDispatch();
  const transportes = useSelector(selectAllTransportes);
  const currentSeason = useSelector(selectSeason);
  const { ability } = useContext(AbilityContext);
  const toast = useToast();
  
  const [orderCreationType, setOrderCreationType] = useState('puntual');
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    temporada: temporada?.year || new Date().getFullYear(),
    semana: temporada?.week || 1,
    fecha_despacho: '',
    tipo_despacho: 'Delivery',
    tipo: 'Exportación',
    transporte_id: '',
    observacion: '',
    pedido_recurrente_id: null,
    detalles: []
  });

  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [transportesFiltrados, setTransportesFiltrados] = useState([]);
  const [loadingFromRecurring, setLoadingFromRecurring] = useState(false);
  const [stockData, setStockData] = useState({});
  const [loadingStock, setLoadingStock] = useState(false);
  const [openInventoryPopover, setOpenInventoryPopover] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, flip: false });

  const [errors, setErrors] = useState({});
  
  const popoverRef = useRef(null);

  // Load transportes on mount
  useEffect(() => {
    dispatch(fetchTransportes());
  }, [dispatch]);

  // Filtrar clientes y transportes según el tipo seleccionado
  useEffect(() => {
    if (formData.tipo === 'Exportación') {
      setClientesFiltrados(clientes.filter(c => c.pais === 'USA' || c.pais === 'Canada'));
      const filtered = transportes.filter(t => t.tipo === 'exportacion');
      setTransportesFiltrados(filtered);
      console.log('Filtered transportes (Exportación):', filtered);
      console.log('Current transporte_id:', formData.transporte_id);
    } else {
      setClientesFiltrados(clientes.filter(c => c.pais === 'CL'));
      const filtered = transportes.filter(t => t.tipo === 'nacional');
      setTransportesFiltrados(filtered);
      console.log('Filtered transportes (Nacional):', filtered);
      console.log('Current transporte_id:', formData.transporte_id);
    }
  }, [formData.tipo, clientes, transportes, formData.transporte_id]);

  // Función para obtener stock de productos
  const fetchStockForProducts = async (productIds) => {
    if (!productIds || productIds.length === 0) {
      setStockData({});
      return;
    }

    setLoadingStock(true);
    try {
      const { data } = await inventarioAPI.getByProductIds(productIds);
      
      // Convert array to map for easy lookup
      const stockMap = {};
      data.forEach(item => {
        stockMap[item.producto_id] = {
          stock_campo: item.stock_campo,
          stock_warehouse_usa: item.stock_warehouse_usa
        };
      });
      
      setStockData(stockMap);
    } catch (error) {
      console.error('Error fetching stock data:', error);
      setStockData({});
    } finally {
      setLoadingStock(false);
    }
  };

  // Fetch stock when detalles change
  useEffect(() => {
    const productIds = formData.detalles
      .map(d => parseInt(d.producto_id))
      .filter(id => !isNaN(id) && id > 0);
    
    if (productIds.length > 0) {
      fetchStockForProducts(productIds);
    } else {
      setStockData({});
    }
  }, [formData.detalles]);

  // Inventory popover handlers
  const handleInventoryClick = (index, event) => {
    if (openInventoryPopover === index) {
      setOpenInventoryPopover(null);
      return;
    }

    const button = event.currentTarget;
    const buttonRect = button.getBoundingClientRect();
    const modalContainer = button.closest('.modal-container');
    const modalRect = modalContainer?.getBoundingClientRect();

    if (modalRect) {
      const spaceOnRight = modalRect.right - buttonRect.right;
      const popoverWidth = 280;
      const shouldFlip = spaceOnRight < popoverWidth;

      setPopoverPosition({
        top: buttonRect.top - modalRect.top + buttonRect.height / 2,
        left: shouldFlip 
          ? buttonRect.left - modalRect.left - popoverWidth - 8
          : buttonRect.right - modalRect.left + 8,
        flip: shouldFlip
      });
    }

    setOpenInventoryPopover(index);
  };

  const handleClosePopover = () => {
    setOpenInventoryPopover(null);
  };

  // Click-outside detection for popover
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        const isInventoryButton = event.target.closest('.inventory-view-button');
        if (!isInventoryButton) {
          handleClosePopover();
        }
      }
    };

    if (openInventoryPopover !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openInventoryPopover]);

  // Reset del formulario cuando el modal se abre/cierra
  useEffect(() => {
    if (!isOpen) {
      // Limpiar errores cuando se cierra el modal
      setErrors({});
      // Reset order creation type
      setOrderCreationType('puntual');
      return;
    }

    if (order) {
      // Modo edición
      console.log('Order data:', order);
      console.log('Cliente ID:', order.cliente_id, 'Type:', typeof order.cliente_id);
      console.log('CustomerId:', order.customerId, 'Type:', typeof order.customerId);
      
      // Determinar el transporte_id (puede venir directo o en la relación)
      const transporteId = order.transporte_id || order.transportes?.id || '';
      
      console.log('Initializing order edit mode:');
      console.log('- Order transporte_id:', order.transporte_id);
      console.log('- Order transportes:', order.transportes);
      console.log('- Resolved transporteId:', transporteId);
      
      // Calculate default fecha_despacho if not set
      const orderYear = order.temporada || temporada?.year || new Date().getFullYear();
      const orderWeek = order.semana || temporada?.week || 1;
      const defaultFechaDespacho = order.fecha_despacho || getFirstBusinessDayOfWeek(orderYear, orderWeek);
      
      // Usar precios guardados en el pedido
      const updatedDetalles = (order.detalles || []).map(detalle => ({
        ...detalle,
        producto_id: detalle.producto_id || detalle.producto?.id,
        precio_unitario: parseFloat(detalle.precio_unitario)
      }));
      
      setFormData({
        cliente_id: order.customerId ? String(order.customerId) : (order.cliente_id ? String(order.cliente_id) : ''),
        temporada: orderYear,
        semana: orderWeek,
        fecha_despacho: defaultFechaDespacho,
        tipo_despacho: order.tipo_despacho || 'Delivery',
        tipo: order.tipo || 'Exportación',
        transporte_id: transporteId ? String(transporteId) : '',
        observacion: order.observacion || '',
        pedido_recurrente_id: order.pedido_recurrente_id || null,
        detalles: updatedDetalles
      });
    } else {
      // Modo creación - agregar una línea vacía por defecto y calcular fecha por defecto
      const defaultYear = temporada?.year || new Date().getFullYear();
      const defaultWeek = temporada?.week || 1;
      const defaultFechaDespacho = getFirstBusinessDayOfWeek(defaultYear, defaultWeek);
      
      // Reset order creation type to 'puntual' for new orders
      setOrderCreationType('puntual');
      
      setFormData({
        cliente_id: '',
        temporada: defaultYear,
        semana: defaultWeek,
        fecha_despacho: defaultFechaDespacho,
        tipo_despacho: 'Delivery',
        tipo: 'Exportación',
        transporte_id: '',
        observacion: '',
        pedido_recurrente_id: null,
        detalles: [{ producto_id: '', cantidad_cajas: 1, precio_unitario: 0 }]
      });
    }
  }, [isOpen, order, temporada, productos]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
    
    // Si cambia el tipo, resetear el cliente y transporte seleccionado
    // Y actualizar los precios de los productos existentes
    if (field === 'tipo') {
      setFormData(prev => {
        // Actualizar precios de productos existentes según el nuevo tipo
        const updatedDetalles = prev.detalles.map(detalle => {
          if (!detalle.producto_id) return detalle;
          
          const producto = productos.find(p => p.id === parseInt(detalle.producto_id));
          if (!producto) return detalle;
          
          // Usar precio_unitario para Nacional, precio_fob_mia para Exportación
          const priceField = value === 'Exportación' ? 'precio_fob_mia' : 'precio_unitario';
          const precio = producto[priceField];
          
          return {
            ...detalle,
            precio_unitario: precio ? parseFloat(precio) : detalle.precio_unitario
          };
        });
        
        return {
          ...prev,
          cliente_id: '',
          transporte_id: '',
          detalles: updatedDetalles
        };
      });
    }
    
    // Si cambia el cliente, autocompletar transporte desde el cliente
    if (field === 'cliente_id' && value) {
      const cliente = clientes.find(c => String(c.id) === String(value));
      if (cliente && cliente.transporte_id) {
        setFormData(prev => ({ ...prev, transporte_id: cliente.transporte_id }));
      }
    }
  };

  const handleOrderTypeChange = (type) => {
    setOrderCreationType(type);
    
    // Clear recurring order selection and reset form when switching to 'puntual'
    if (type === 'puntual') {
      setFormData(prev => ({
        ...prev,
        pedido_recurrente_id: null,
        cliente_id: '',
        tipo: 'Exportación',
        transporte_id: '',
        observacion: '',
        detalles: [{ producto_id: '', cantidad_cajas: 1, precio_unitario: 0 }]
      }));
    }
  };

  const handleLoadFromRecurring = async (pedidoRecurrenteId) => {
    if (!pedidoRecurrenteId) {
      setFormData(prev => ({
        ...prev,
        pedido_recurrente_id: null,
        detalles: [{ producto_id: '', cantidad_cajas: 1, precio_unitario: 0 }]
      }));
      return;
    }

    setLoadingFromRecurring(true);
    try {
      const { pedidosRecurrentesAPI } = await import('../../services/api');
      const { data } = await pedidosRecurrentesAPI.getById(parseInt(pedidoRecurrenteId));
      
      if (data) {
        // Usar precios guardados en el pedido recurrente
        const updatedDetalles = data.detalles.map(d => ({
          producto_id: d.producto_id,
          cantidad_cajas: d.cantidad_cajas,
          precio_unitario: parseFloat(d.precio_unitario)
        }));
        
        setFormData(prev => ({
          ...prev,
          cliente_id: String(data.cliente_id),
          tipo: data.tipo,
          pedido_recurrente_id: data.id,
          detalles: updatedDetalles
        }));
      }
    } catch (error) {
      console.error('Error cargando pedido recurrente:', error);
      alert('Error al cargar el pedido recurrente');
    } finally {
      setLoadingFromRecurring(false);
    }
  };

  const handleDetalleChange = (index, field, value) => {
    const newDetalles = [...formData.detalles];
    newDetalles[index] = { ...newDetalles[index], [field]: value };
    
    // Si cambia el producto, actualizar el precio unitario automáticamente según el tipo de pedido
    if (field === 'producto_id') {
      const producto = productos.find(p => p.id === parseInt(value));
      if (producto) {
        // Usar precio_unitario para Nacional, precio_fob_mia para Exportación
        const priceField = getPriceField(formData.tipo);
        const precio = producto[priceField];
        if (precio) {
          newDetalles[index].precio_unitario = parseFloat(precio);
        }
      }
    }
    
    setFormData(prev => ({ ...prev, detalles: newDetalles }));
  };

  const addDetalle = () => {
    setFormData(prev => ({
      ...prev,
      detalles: [...prev.detalles, { producto_id: '', cantidad_cajas: 1, precio_unitario: 0 }]
    }));
  };

  const removeDetalle = (index) => {
    if (formData.detalles.length === 1) {
      alert('Debe haber al menos un producto en el pedido');
      return;
    }
    setFormData(prev => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index)
    }));
  };

  const calculateLineTotal = (detalle) => {
    if (!detalle.producto_id) return 0;
    
    const producto = productos.find(p => p.id === parseInt(detalle.producto_id));
    if (!producto) return 0;
    
    const cantidad = parseFloat(detalle.cantidad_cajas) || 0;
    const precioManual = parseFloat(detalle.precio_unitario) || 0;
    
    if (cantidad <= 0 || precioManual <= 0) return 0;
    
    try {
      // Aplicar fórmulas centralizadas pero con el precio modificado manualmente:
      // Exportación: precio_manual × ramos_por_caja × cajas
      // Nacional: precio_manual × ramos_por_caja × tallos_por_ramo × cajas
      const ramosPorCaja = Number(producto.ramos_por_caja || producto.bunches_box) || 0;
      
      if (ramosPorCaja <= 0) return 0;
      
      if (formData.tipo === 'Exportación') {
        return precioManual * ramosPorCaja * cantidad;
      } else {
        const tallosPorRamo = Number(producto.tallos_por_ramo || producto.stems_bunches) || 0;
        if (tallosPorRamo <= 0) return 0;
        return precioManual * ramosPorCaja * tallosPorRamo * cantidad;
      }
    } catch (error) {
      console.error('Error calculating line total:', error);
      return 0;
    }
  };

  const calculateOrderTotal = () => {
    return formData.detalles.reduce((sum, detalle) => {
      return sum + calculateLineTotal(detalle);
    }, 0);
  };

  const validate = () => {
    const newErrors = {};

    // Validate recurring order selection if type is 'recurrente'
    if (!order && orderCreationType === 'recurrente' && !formData.pedido_recurrente_id) {
      newErrors.pedido_recurrente_id = 'Debe seleccionar un pedido recurrente';
    }

    if (!formData.cliente_id) {
      newErrors.cliente_id = 'Seleccione un cliente';
    }

    if (!formData.fecha_despacho) {
      newErrors.fecha_despacho = 'Ingrese una fecha de despacho';
    }

    if (!formData.transporte_id) {
      newErrors.transporte_id = 'Seleccione un transporte';
    }

    if (formData.detalles.length === 0) {
      newErrors.detalles = 'Agregue al menos un producto';
    } else {
      // Validate product details and show single error message
      for (let index = 0; index < formData.detalles.length; index++) {
        const detalle = formData.detalles[index];
        if (!detalle.producto_id) {
          newErrors.detalles = `Línea ${index + 1}: Debe seleccionar un producto`;
          break;
        }
        if (!detalle.cantidad_cajas || detalle.cantidad_cajas <= 0) {
          newErrors.detalles = `Línea ${index + 1}: La cantidad debe ser mayor a 0`;
          break;
        }
        if (!detalle.precio_unitario || detalle.precio_unitario <= 0) {
          newErrors.detalles = `Línea ${index + 1}: El precio debe ser mayor a 0`;
          break;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    // Check weekly limits for export orders (unless user has bypass permission)
    const canBypassLimits = ability && ability.can('bypass-limits', 'orders:weekly');
    
    if (formData.tipo === 'Exportación' && !canBypassLimits) {
      // Filter export orders only and enrich with product data
      const exportOrders = existingOrders
        .filter(o => o.tipo === 'Exportación')
        .map(order => ({
          ...order,
          detalles: order.detalles?.map(detalle => {
            const producto = productos.find(p => p.id === (detalle.producto_id || detalle.producto?.id));
            return {
              ...detalle,
              peso_total_caja_kg: producto?.peso_total_caja_kg || 0,
              volumen_m3: producto?.volumen_m3 || 0
            };
          }) || []
        }));
      
      // Prepare detalles with product data for validation
      const detallesWithProductData = formData.detalles.map(detalle => {
        const producto = productos.find(p => p.id === parseInt(detalle.producto_id));
        return {
          ...detalle,
          peso_total_caja_kg: producto?.peso_total_caja_kg || 0,
          volumen_m3: producto?.volumen_m3 || 0
        };
      });

      const limitCheck = checkOrderLimits(
        exportOrders,
        detallesWithProductData,
        order?.id || null
      );

      if (limitCheck.exceedsWeight) {
        toast.error(
          `No se puede guardar el pedido. El peso total semanal excedería el límite de ${limitCheck.limits.weight} kg. ` +
          `Total actual: ${limitCheck.currentTotals.totalWeight} kg | Este pedido: ${limitCheck.currentOrderTotals.weight} kg | Nuevo total: ${limitCheck.newTotals.weight} kg`,
          8000
        );
        return;
      }

      if (limitCheck.exceedsVolume) {
        toast.error(
          `No se puede guardar el pedido. El volumen total semanal excedería el límite de ${limitCheck.limits.volume} m³. ` +
          `Total actual: ${limitCheck.currentTotals.totalVolume} m³ | Este pedido: ${limitCheck.currentOrderTotals.volume} m³ | Nuevo total: ${limitCheck.newTotals.volume} m³`,
          8000
        );
        return;
      }
    }

    // Preparar datos para enviar
    const orderData = {
      ...formData,
      detalles: formData.detalles.map(detalle => ({
        producto_id: parseInt(detalle.producto_id),
        cantidad_cajas: parseInt(detalle.cantidad_cajas),
        precio_unitario: parseFloat(detalle.precio_unitario),
        precio_total: calculateLineTotal(detalle)
      }))
    };

    onSave(orderData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {readOnly ? 'Ver Pedido' : (order ? 'Editar Pedido' : 'Nuevo Pedido')}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
          {/* Order Type Selection - Only for new orders */}
          {!order && !readOnly && (
            <div className="form-section">
              <h3 className="section-title">Tipo de Pedido</h3>
              
              <div className="order-type-selector">
                <div 
                  className={`order-type-card ${orderCreationType === 'recurrente' ? 'selected' : ''}`}
                  onClick={() => handleOrderTypeChange('recurrente')}
                >
                  <div className="order-type-icon">
                    <Repeat size={24} />
                  </div>
                  <div className="order-type-content">
                    <div className="order-type-title">Pedido Recurrente</div>
                    <div className="order-type-description">
                      Seleccionar plantilla de pedido recurrente con datos pre-cargados
                    </div>
                  </div>
                </div>

                <div 
                  className={`order-type-card ${orderCreationType === 'puntual' ? 'selected' : ''}`}
                  onClick={() => handleOrderTypeChange('puntual')}
                >
                  <div className="order-type-icon">
                    <FileText size={24} />
                  </div>
                  <div className="order-type-content">
                    <div className="order-type-title">Pedido Puntual</div>
                    <div className="order-type-description">
                      Ingresar todos los datos manualmente
                    </div>
                  </div>
                </div>
              </div>

              {/* Recurring Orders Dropdown - Only show when 'recurrente' is selected */}
              {orderCreationType === 'recurrente' && pedidosRecurrentes && pedidosRecurrentes.length > 0 && (
                <div className="recurring-order-dropdown">
                  <label className="form-label">Seleccionar Pedido Recurrente *</label>
                  <select
                    className={`form-select ${errors.pedido_recurrente_id ? 'error' : ''}`}
                    value={formData.pedido_recurrente_id || ''}
                    onChange={(e) => handleLoadFromRecurring(e.target.value)}
                    disabled={loadingFromRecurring}
                  >
                    <option value="">-- Seleccione un pedido recurrente --</option>
                    {pedidosRecurrentes.map(pr => (
                      <option key={pr.id} value={pr.id}>
                        {pr.clientes?.nombre} - Semanas {pr.semana_inicio}-{pr.semana_fin} ({pr.tipo})
                      </option>
                    ))}
                  </select>
                  {errors.pedido_recurrente_id && <span className="error-text">{errors.pedido_recurrente_id}</span>}
                  {loadingFromRecurring && <span className="text-sm text-gray-500">Cargando...</span>}
                </div>
              )}
            </div>
          )}

          {/* Información General */}
          <div className="form-section">
            <h3 className="section-title">Información General</h3>
            
            <div className="form-grid">

              <div className="form-group">
                <label className="form-label">Tipo *</label>
                <select
                  className={`form-select ${errors.tipo ? 'error' : ''}`}
                  value={formData.tipo}
                  onChange={(e) => handleChange('tipo', e.target.value)}
                  disabled={readOnly}
                >
                  <option value="Exportación">Exportación (USA/Canadá)</option>
                  <option value="Nacional">Nacional (Chile)</option>
                </select>
                {errors.tipo && <span className="error-text">{errors.tipo}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Cliente *</label>
                <select
                  className={`form-select ${errors.cliente_id ? 'error' : ''}`}
                  value={formData.cliente_id}
                  onChange={(e) => handleChange('cliente_id', e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Seleccione un cliente</option>
                  {clientesFiltrados.map(cliente => (
                    <option key={cliente.id} value={String(cliente.id)}>
                      {cliente.nombre} - {cliente.pais}
                    </option>
                  ))}
                </select>
                {errors.cliente_id && <span className="error-text">{errors.cliente_id}</span>}
              </div>

              <div className="form-group form-group-third">
                <label className="form-label">Temporada</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.temporada}
                  onChange={(e) => handleChange('temporada', e.target.value)}
                  min="2020"
                  max="2030"
                  disabled
                />
              </div>

              <div className="form-group form-group-third">
                <label className="form-label">Semana</label>
                <input
                  type="number"
                  className="form-input"
                  value={formData.semana}
                  onChange={(e) => handleChange('semana', e.target.value)}
                  min="1"
                  max="53"
                  disabled
                />
              </div>

              <div className="form-group form-group-third">
                <label className="form-label">Fecha entrega USA *</label>
                <div className="date-picker-wrapper">
                  <WeekHighlightDatePicker
                    value={formData.fecha_despacho}
                    onChange={(dateString) => handleChange('fecha_despacho', dateString)}
                    season={currentSeason?.year}
                    week={currentSeason?.week}
                    placeholder="Seleccionar fecha"
                    disabled={readOnly}
                  />
                </div>
                {errors.fecha_despacho && <span className="error-text">{errors.fecha_despacho}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Transporte *</label>
                <select
                  className={`form-select ${errors.transporte_id ? 'error' : ''}`}
                  value={formData.transporte_id || ''}
                  onChange={(e) => handleChange('transporte_id', e.target.value)}
                  disabled={readOnly}
                >
                  <option value="">Seleccione un transporte</option>
                  {transportesFiltrados.map(transporte => (
                    <option key={transporte.id} value={String(transporte.id)}>
                      {transporte.nombre}
                    </option>
                  ))}
                </select>
                {errors.transporte_id && <span className="error-text">{errors.transporte_id}</span>}
              </div>

              <div className="form-group">
                <label className="form-label">Observaciones</label>
                <textarea
                  className="form-input"
                  value={formData.observacion}
                  onChange={(e) => handleChange('observacion', e.target.value)}
                  rows="3"
                  placeholder="Observaciones adicionales del pedido"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Productos */}
          <div className="form-section">
            <div className="section-header">
              <h3 className="section-title">Productos</h3>
              {!readOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={addDetalle}
                >
                  <Plus size={16} />
                  Agregar Producto
                </button>
              )}
            </div>

            {errors.detalles && <span className="error-text">{errors.detalles}</span>}

            <div className="products-table">
              <div className="products-table-header">
                <div className="table-col-number">#</div>
                <div className="table-col-product">Producto</div>
                <div className="table-col-cajas">Cajas</div>
                <div className="table-col-precio">{getPriceLabel(formData.tipo)}</div>
                <div className="table-col-total">Total</div>
                <div className="table-col-actions"></div>
              </div>
              
              <div className="products-table-body">
                {formData.detalles.map((detalle, index) => (
                  <div key={index} className="product-row">
                    <div className="table-col-number">
                      <div className="product-line-number">{index + 1}</div>
                    </div>
                    
                    <div className="table-col-product">
                      {readOnly ? (
                        <div className="form-input-static">
                          {(() => {
                            const producto = productos.find(p => p.id === parseInt(detalle.producto_id));
                            return producto ? `${producto.nombre} (${producto.code})` : '—';
                          })()}
                        </div>
                      ) : (
                        <select
                          className="form-select"
                          value={detalle.producto_id}
                          onChange={(e) => handleDetalleChange(index, 'producto_id', e.target.value)}
                        >
                          <option value="">Seleccione un producto</option>
                          {productos.filter(p => p.activo !== false).map(producto => (
                            <option key={producto.id} value={producto.id}>
                              {producto.nombre} ({producto.code})
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    <div className="table-col-cajas">
                      <input
                        type="number"
                        className="form-input"
                        value={detalle.cantidad_cajas}
                        onChange={(e) => handleDetalleChange(index, 'cantidad_cajas', e.target.value)}
                        min="1"
                        disabled={readOnly}
                      />
                    </div>

                    <div className="table-col-precio">
                      <input
                        type="number"
                        className="form-input"
                        value={detalle.precio_unitario}
                        onChange={(e) => handleDetalleChange(index, 'precio_unitario', e.target.value)}
                        min="0"
                        step="0.01"
                        disabled={readOnly}
                      />
                    </div>

                    <div className="table-col-total">
                      <div className="form-input-static">
                        {formatPrice(calculateLineTotal(detalle), getCurrency(formData.tipo))}
                      </div>
                    </div>

                    <div className="table-col-actions">
                      {!readOnly && (
                        <>
                          {detalle.producto_id && (() => {
                            const producto = productos.find(p => p.id === parseInt(detalle.producto_id));
                            return producto?.category !== 'Despacho' && (
                              <button
                                type="button"
                                className="inventory-view-button"
                                onClick={(e) => handleInventoryClick(index, e)}
                                disabled={loadingStock}
                                title="Ver Inventario"
                              >
                                <Eye size={18} />
                              </button>
                            );
                          })()}
                          <button
                            type="button"
                            className="product-line-remove"
                            onClick={() => removeDetalle(index)}
                            disabled={formData.detalles.length === 1}
                          >
                            <Trash2 size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Inventory Popover */}
            {openInventoryPopover !== null && (
              <div 
                ref={popoverRef}
                className="inventory-popover"
                style={{
                  position: 'absolute',
                  top: `${popoverPosition.top}px`,
                  left: `${popoverPosition.left}px`,
                  transform: 'translateY(-50%)'
                }}
              >
                <div className="inventory-popover-header">
                  <span className="inventory-popover-title">Inventario</span>
                  <button
                    type="button"
                    className="inventory-popover-close"
                    onClick={handleClosePopover}
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="inventory-popover-content">
                  {(() => {
                    const detalle = formData.detalles[openInventoryPopover];
                    const producto = productos.find(p => p.id === parseInt(detalle?.producto_id));
                    const stock = detalle?.producto_id ? stockData[detalle.producto_id] : null;
                    
                    return (
                      <>
                        <div className="inventory-popover-product">
                          {producto ? `${producto.nombre} (${producto.code})` : '—'}
                        </div>
                        <div className="inventory-popover-row">
                          <span className="inventory-popover-label">I. Campo:</span>
                          <span className="inventory-popover-value">
                            {loadingStock ? '...' : (stock?.stock_campo ?? '—')}
                          </span>
                        </div>
                        <div className="inventory-popover-row">
                          <span className="inventory-popover-label">I. Warehouse USA:</span>
                          <span className="inventory-popover-value">
                            {loadingStock ? '...' : (stock?.stock_warehouse_usa ?? '—')}
                          </span>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Total del Pedido */}
          <div className="order-total">
            <div className="total-label">Total Pedido:</div>
            <div className="total-amount">{formatPrice(calculateOrderTotal(), getCurrency(formData.tipo))}</div>
          </div>
          </div>

          {/* Botones de Acción */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline"
              onClick={onClose}
            >
              {readOnly ? 'Cerrar' : 'Cancelar'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="btn btn-primary"
              >
                {order ? 'Actualizar Pedido' : 'Crear Pedido'}
              </button>
            )}
          </div>
        </form>

        <style jsx>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: var(--spacing-4);
          }

          .modal-container {
            background: white;
            border-radius: var(--radius-lg);
            width: 100%;
            max-width: 900px;
            max-height: 90vh;
            display: flex;
            flex-direction: column;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-6);
            border-bottom: 1px solid var(--gray-200);
            background: linear-gradient(135deg, var(--secondary-green) 0%, #1a4d2e 100%);
          }

          .modal-title {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: white;
            margin: 0;
          }

          .modal-close {
            padding: var(--spacing-2);
            border: none;
            background: rgba(255, 255, 255, 0.2);
            cursor: pointer;
            color: white;
            border-radius: var(--radius-md);
            transition: all 0.2s;
          }

          .modal-close:hover {
            background-color: rgba(255, 255, 255, 0.3);
          }

          .modal-body {
            padding: var(--spacing-5);
            overflow-y: auto;
            flex: 1;
            max-height: calc(90vh - 180px);
          }
          
          .date-picker-wrapper {
            position: relative;
            z-index: 100;
          }
          
          .date-picker-wrapper :global(.react-datepicker-popper) {
            z-index: 9999 !important;
          }
          
          .modal-footer {
            position: relative;
            z-index: 1;
          }

          .form-section {
            margin-bottom: var(--spacing-8);
          }

          .section-title {
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-900);
            margin-bottom: var(--spacing-4);
          }

          .section-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: var(--spacing-4);
          }

          .form-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: var(--spacing-4);
          }

          .form-group {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
          }

          .form-group.col-span-2 {
            grid-column: span 2;
          }

          .form-group.form-group-third {
            grid-column: span 1;
          }

          /* Fila de 3 columnas para Temporada, Semana, Fecha */
          .form-grid .form-group-third:nth-of-type(3),
          .form-grid .form-group-third:nth-of-type(4),
          .form-grid .form-group-third:nth-of-type(5) {
            grid-column: auto;
          }

          .form-label {
            font-size: var(--font-size-sm);
            font-weight: 500;
            color: var(--gray-700);
          }

          /* Order Type Selector Styles */
          .order-type-selector {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: var(--spacing-4);
            margin-bottom: var(--spacing-6);
          }

          .order-type-card {
            display: flex;
            align-items: flex-start;
            gap: var(--spacing-3);
            padding: var(--spacing-4);
            border: 2px solid var(--gray-300);
            border-radius: var(--radius-lg);
            background-color: white;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .order-type-card:hover {
            border-color: var(--secondary-green);
            background-color: #e8f5e9;
            transform: translateY(-2px);
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }

          .order-type-card.selected {
            border-color: var(--secondary-green);
            background-color: #e8f5e9;
            box-shadow: 0 0 0 3px rgba(44, 95, 45, 0.1);
          }

          .order-type-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            border-radius: var(--radius-md);
            background-color: white;
            color: var(--secondary-green);
            flex-shrink: 0;
            transition: all 0.2s ease;
          }

          .order-type-card.selected .order-type-icon {
            background-color: var(--secondary-green);
            color: white;
          }

          .order-type-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-1);
          }

          .order-type-title {
            font-size: var(--font-size-base);
            font-weight: 600;
            color: var(--gray-900);
          }

          .order-type-description {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            line-height: 1.4;
          }

          .recurring-order-dropdown {
            margin-top: var(--spacing-4);
            padding: var(--spacing-4);
            background-color: #f0f9ff;
            border: 1px solid #bae6fd;
            border-radius: var(--radius-md);
            animation: slideDown 0.2s ease;
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .form-input,
          .form-select {
            padding: var(--spacing-3);
            border: 2px solid var(--gray-300);
            border-radius: var(--radius-md);
            font-size: var(--font-size-base);
            transition: border-color 0.2s;
          }

          .form-input:focus,
          .form-select:focus {
            outline: none;
            border-color: var(--primary-blue);
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }

          .form-input.error,
          .form-select.error {
            border-color: var(--error);
          }

          .error-text {
            font-size: var(--font-size-sm);
            color: var(--error);
          }

          .error-message {
            font-size: var(--font-size-sm);
            color: var(--error);
            margin-top: var(--spacing-1);
          }

          .form-input-static {
            padding: 6px var(--spacing-2);
            background-color: white;
            border: 1px solid var(--gray-300);
            border-radius: var(--radius-md);
            font-size: var(--font-size-sm);
            font-weight: 400;
            color: var(--gray-900);
            display: flex;
            align-items: center;
            height: 32px;
            line-height: 1.5;
            box-sizing: border-box;
            font-family: inherit;
          }

          .products-table {
            border: 1px solid var(--gray-200);
            border-radius: var(--radius-md);
            overflow-x: auto;
          }

          .products-table-header {
            display: grid;
            grid-template-columns: 40px 2fr 90px 110px 110px 80px;
            gap: var(--spacing-2);
            padding: var(--spacing-2) var(--spacing-3);
            background-color: var(--gray-100);
            border-bottom: 2px solid var(--gray-300);
            font-size: 11px;
            font-weight: 600;
            color: var(--gray-700);
            text-transform: uppercase;
            letter-spacing: 0.5px;
            min-width: 620px;
          }

          .products-table-body {
            display: flex;
            flex-direction: column;
          }

          .product-row {
            display: grid;
            grid-template-columns: 40px 2fr 90px 110px 110px 80px;
            gap: var(--spacing-2);
            padding: var(--spacing-2) var(--spacing-3);
            border-bottom: 1px solid var(--gray-200);
            background-color: white;
            align-items: center;
            min-width: 620px;
          }

          .product-row:last-child {
            border-bottom: none;
          }

          .product-row:hover {
            background-color: var(--gray-50);
          }

          .table-col-number {
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .product-line-number {
            width: 24px;
            height: 24px;
            background-color: var(--secondary-green);
            color: white;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: var(--font-size-xs);
          }

          .table-col-product {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .table-col-cajas,
          .table-col-precio,
          .table-col-total {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }

          .table-col-actions {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 4px;
          }

          .inventory-view-button {
            padding: 6px;
            border: none;
            background-color: transparent;
            color: var(--primary-blue);
            cursor: pointer;
            border-radius: var(--radius-md);
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .inventory-view-button:hover:not(:disabled) {
            background-color: var(--primary-blue);
            color: white;
          }

          .inventory-view-button:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }

          .product-line-remove {
            padding: 6px;
            border: none;
            background-color: transparent;
            color: var(--error);
            cursor: pointer;
            border-radius: var(--radius-md);
            transition: all 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .product-line-remove:hover:not(:disabled) {
            background-color: var(--error);
            color: white;
          }

          .product-line-remove:disabled {
            opacity: 0.3;
            cursor: not-allowed;
          }

          .inventory-popover {
            position: absolute;
            z-index: 1000;
            background: white;
            border: 1px solid var(--gray-300);
            border-radius: var(--radius-md);
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
            width: 280px;
            animation: popoverFadeIn 0.15s ease-out;
          }

          @keyframes popoverFadeIn {
            from {
              opacity: 0;
              transform: translateY(-50%) scale(0.95);
            }
            to {
              opacity: 1;
              transform: translateY(-50%) scale(1);
            }
          }

          .inventory-popover-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-3);
            border-bottom: 1px solid var(--gray-200);
            background-color: var(--gray-50);
            border-radius: var(--radius-md) var(--radius-md) 0 0;
          }

          .inventory-popover-title {
            font-size: var(--font-size-sm);
            font-weight: 600;
            color: var(--gray-900);
          }

          .inventory-popover-close {
            padding: 4px;
            border: none;
            background: transparent;
            color: var(--gray-500);
            cursor: pointer;
            border-radius: var(--radius-sm);
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
          }

          .inventory-popover-close:hover {
            background-color: var(--gray-200);
            color: var(--gray-700);
          }

          .inventory-popover-content {
            padding: var(--spacing-3);
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);
          }

          .inventory-popover-product {
            font-size: var(--font-size-sm);
            font-weight: 600;
            color: var(--gray-900);
            padding-bottom: var(--spacing-2);
            border-bottom: 1px solid var(--gray-200);
            margin-bottom: var(--spacing-1);
          }

          .inventory-popover-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: var(--spacing-2) 0;
          }

          .inventory-popover-label {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            font-weight: 500;
          }

          .inventory-popover-value {
            font-size: var(--font-size-sm);
            color: var(--gray-900);
            font-weight: 600;
          }

          .order-total {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: var(--spacing-4);
            padding: var(--spacing-4);
            background-color: var(--gray-100);
            border-radius: var(--radius-lg);
            margin-top: var(--spacing-4);
          }

          .total-label {
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-700);
          }

          .total-amount {
            font-size: var(--font-size-2xl);
            font-weight: 700;
            color: var(--secondary-green);
          }

          .modal-footer {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-3);
            padding: var(--spacing-4);
            border-top: 1px solid var(--gray-200);
            background: var(--gray-50);
            border-radius: 0 0 var(--radius-lg) var(--radius-lg);
          }

          @media (max-width: 768px) {
            .modal-container {
              max-width: 100%;
              max-height: 100vh;
              border-radius: 0;
            }

            .order-type-selector {
              grid-template-columns: 1fr;
            }

            .order-type-card {
              min-height: 80px;
            }

            .form-grid {
              grid-template-columns: 1fr;
            }

            .products-table-header {
              display: none;
            }

            .product-row {
              grid-template-columns: 30px 1fr 40px;
              grid-template-areas:
                "number product actions"
                "number cajas actions"
                "number precio actions"
                "number total actions";
              gap: var(--spacing-1);
              padding: var(--spacing-3);
            }

            .table-col-number {
              grid-area: number;
            }

            .table-col-product {
              grid-area: product;
            }

            .table-col-cajas {
              grid-area: cajas;
            }

            .table-col-cajas::before {
              content: "Cajas: ";
              font-size: 11px;
              font-weight: 600;
              color: var(--gray-600);
              margin-right: 4px;
            }

            .table-col-precio {
              grid-area: precio;
            }

            .table-col-precio::before {
              content: "Precio: ";
              font-size: 11px;
              font-weight: 600;
              color: var(--gray-600);
              margin-right: 4px;
            }

            .table-col-total {
              grid-area: total;
            }

            .table-col-total::before {
              content: "Total: ";
              font-size: 11px;
              font-weight: 600;
              color: var(--gray-600);
              margin-right: 4px;
            }

            .table-col-actions {
              grid-area: actions;
              align-self: start;
            }

            .table-col-cajas,
            .table-col-precio,
            .table-col-total {
              flex-direction: row;
              align-items: center;
            }

            .form-group.col-span-2,
            .form-group.form-group-third {
              grid-column: span 1;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default OrderModal;
